import styled, { css } from "styled-components";
import UserAddOutlined from "@ant-design/icons/UserAddOutlined";
import React, { CSSProperties, useState } from "react";
import LinkButton from "common/Buttons/LinkButton.tsx";
import ManageInvitationsModal from "features/configuration/InviteExternalUsers/ManageInvitationsModal.tsx";
import { useUser } from "@shared/utils/hooks/misc.ts";
import {
  CREATE_ACCOUNT_INVITE_FEATURE_FLAG,
  IS_WHOP,
} from "common/constants.ts";
import Text from "common/Typography/Text";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";
import { LanguageNameSpaces, UsageType, WorkspaceType } from "@shared/types";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import SecureClientInviteHelperTooltip from "features/settings/Billing/SecureClientInviteHelperTooltip.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc.ts";

interface InviteExternalUserProps {
  style?: CSSProperties;
}

const InviteExternalUser = ({ style }: InviteExternalUserProps) => {
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { user } = useUser();
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const workspace = useSelector(selectWorkspace);

  if (
    !user ||
    (!user?.hasFeatureFlag(CREATE_ACCOUNT_INVITE_FEATURE_FLAG) &&
      !isWhitelabelSubscriptionType(user?.subscription_plan_type)) ||
    user.is_onboarding ||
    workspace === WorkspaceType.DEMO ||
    IS_WHOP
  )
    return null;

  return (
    <>
      <Wrapper style={style}>
        <Text as="div">
          <UserAddOutlined style={{ marginRight: 8 }} />
          {tCommon(
            getUsageTitleKey(
              UsageType.SECURE_CLIENT_INVITE,
              user?.subscription_plan_type
            )
          )}
          <SecureClientInviteHelperTooltip
            planType={user?.subscription_plan_type}
          />
        </Text>
        <LinkButton
          type="link"
          onClick={() => setManageModalOpen(true)}
          style={{ paddingRight: 0 }}
        >
          {t("buttons.manage_invites")}
        </LinkButton>
      </Wrapper>
      <ManageInvitationsModal
        open={manageModalOpen}
        onCancel={() => setManageModalOpen(false)}
      />
    </>
  );
};

export default InviteExternalUser;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: ${(p) => p.theme.colors.statisticsBgColor};
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-height: 48px;
      padding: 6px 12px 6px 16px;
      border-radius: 12px;
      background-color: ${p.theme.colors.cardLightBgColor};
      font-size: 13px;
      .anticon {
        color: ${p.theme.colors.secondaryText};
      }
    `}
`;
